import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import dotsIcon from 'assets/images/icons/table-icons.svg';
import dropdownIcon from 'assets/images/icons/dropdown-arrow-white.svg';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import FormButton from 'components/form-components/FormButton';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';

const ScheduleTasksActions = ({ ids, actionsCallBack, isActionButton }) => {
  const { t } = useTranslation();

  const [actionsOptions, setActionsOptions] = useState([]);
  const [confirmModalProps, setConfirmModalProps] = useState(null);

  const isAllowedEdit = () => {
    return ids.length === 1 && ids[0].status === 'todo' && new Date(ids[0].startDate) > new Date();
  };

  const isAllowedClosed = () => {
    return ids.every(
      (row) => row.status === 'todo' && (!row.startDate || new Date(row.startDate) > new Date()),
    );
  };

  const isAllowedDuplicate = () => {
    return ids.length === 1 && ids[0].startDate && ids[0].scheduleType === 'recurring';
  };

  useEffect(() => {
    const allOptions = [
      { value: 'edit', displayValue: 'select_edit', permission: isAllowedEdit() },
      { value: 'close', displayValue: 'select_close', permission: isAllowedClosed() },
      { value: 'duplicate', displayValue: 'select_duplicate', permission: isAllowedDuplicate() },
      { value: 'delete', displayValue: 'select_delete', permission: true },
    ];

    const visibleOptions = allOptions.filter((option) => option.permission);

    setActionsOptions(visibleOptions);
  }, [ids.length]);

  const handleAction = (_, value) => {
    if (value === 'edit' || value === 'duplicate') actionsCallBack(value, { id: ids[0].id });
    else if (value === 'close' || value === 'delete')
      setConfirmModalProps({
        type: value,
        title: value === 'close' ? 'alert_task_close_title' : 'alert_task_deletion_title',
        message: value === 'close' ? 'alert_task_close_message' : 'alert_task_deletion_message',
        text: value === 'close' ? 'button_close' : 'button_delete',
      });
  };

  const handleConfirmAction = () => {
    if (confirmModalProps.type === 'close' || confirmModalProps.type === 'delete')
      actionsCallBack(confirmModalProps.type, { ids: ids.map((i) => i.id) });

    setConfirmModalProps(null);
  };

  return (
    <>
      {confirmModalProps && (
        <ConfirmationModal
          show={!!confirmModalProps}
          title={t(confirmModalProps.title)}
          message={t(confirmModalProps.message)}
          actionList={[
            {
              color: 'black-1',
              text: t('button_cancel'),
              onClick: () => setConfirmModalProps(null),
            },
            {
              color: 'red-1',
              text: t(confirmModalProps.text),
              onClick: () => handleConfirmAction(),
            },
          ]}
        />
      )}
      <MultiLevelSelect
        options={{ children: actionsOptions }}
        value={[]}
        name='action'
        handleChange={(name, value) => handleAction(name, value[0])}
        toggleClasses=''
        toggleComponent={
          <>
            {isActionButton ? (
              <img className='cursor-pointer' src={dotsIcon} />
            ) : (
              <FormButton text='button_action' variant='green-1' icon={dropdownIcon} minWidth />
            )}
          </>
        }
        disabled={!ids.length || !actionsOptions.length}
      />
    </>
  );
};

ScheduleTasksActions.propTypes = {
  ids: PropTypes.array.isRequired,
  actionsCallBack: PropTypes.func.isRequired,
  isActionButton: PropTypes.bool.isRequired,
};

export default ScheduleTasksActions;
